import { apiRequest } from '@scc/lib'

export type OAuthPlatform = 'tiktok_shop' | 'tiktok_business'

export interface ConnectionSummary {
  platform: OAuthPlatform
  externalAccountId: string | null
  currency: string | null
  isActive: boolean
  connectedAt: string
  lastSyncedAt: string
  needsSetup: boolean
}

export interface ConnectionStatus {
  connected: boolean
  valid?: boolean
  externalAccountId?: string | null
  currency?: string | null
  connectedAt?: string
}

export interface OAuthAccount {
  accountId: string
  accountName: string
  currency: string | null
}

export interface OAuthAccountList {
  accounts: OAuthAccount[]
  selectedAccountId: string | null
  needsSelection: boolean
}

function storeHeader(storeId: number): Record<string, string> {
  return { 'X-Store-Id': String(storeId) }
}

export const oauthApi = {
  /** Begin OAuth — returns the provider authorize URL the browser must navigate to. */
  async startAuthorize(
    platform: OAuthPlatform,
    storeId: number,
    returnTo?: string,
  ): Promise<{ url: string }> {
    const qs = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''
    return apiRequest<{ url: string }>(`/oauth/${platform}/authorize${qs}`, {
      headers: storeHeader(storeId),
    })
  },

  async disconnect(platform: OAuthPlatform, storeId: number): Promise<void> {
    await apiRequest<{ disconnected: true }>(`/oauth/${platform}/disconnect`, {
      method: 'POST',
      headers: storeHeader(storeId),
    })
  },

  async getStatus(platform: OAuthPlatform, storeId: number): Promise<ConnectionStatus> {
    return apiRequest<ConnectionStatus>(`/oauth/${platform}/status`, {
      headers: storeHeader(storeId),
    })
  },

  async listConnections(storeId: number): Promise<ConnectionSummary[]> {
    return apiRequest<ConnectionSummary[]>(`/oauth/connections`, {
      headers: storeHeader(storeId),
    })
  },

  async listAccounts(platform: OAuthPlatform, storeId: number): Promise<OAuthAccountList> {
    return apiRequest<OAuthAccountList>(`/oauth/${platform}/accounts`, {
      headers: storeHeader(storeId),
    })
  },

  async selectAccount(
    platform: OAuthPlatform,
    storeId: number,
    input: { accountId: string; accountName?: string; currency?: string },
  ): Promise<{ connectionId: number; accountId: string }> {
    return apiRequest<{ connectionId: number; accountId: string }>(
      `/oauth/${platform}/select-account`,
      {
        method: 'POST',
        headers: storeHeader(storeId),
        body: input,
      },
    )
  },
}

/** Map the onboarding store's PlatformKey to the backend's platform identifier. */
export function platformKeyToOAuth(key: 'shop' | 'ads'): OAuthPlatform {
  return key === 'shop' ? 'tiktok_shop' : 'tiktok_business'
}
